import {useContext, useEffect, useRef, useState} from "react";
import {UploadRounded} from "@mui/icons-material";
import {Button, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useRecoilState} from "recoil";
import {AxiosError} from "axios";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {attachmentsTypeState} from "../../state";
import {fetchExpiredAttachmentsTypes, fetchExpiredAttachmentsUpload} from "../../services/http.services";
import {resizeImg} from "../../utils";
import CustomLoader from "../../../../util/component/CustomLoader";
import {TButtonVariant} from "../../models/models";
import { setSnackbar } from "../../../../util/state/globalAction";

const UploadAttachment = (props: {
    expiredCode: string,
    postAction?: Function,
    variant?: TButtonVariant,
    label?: string,
    endIcon?: boolean,
    showSnackbar?: boolean
}) => {
    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement', 'translation']);
    const {dispatchGlobalData} = useContext(GlobalContext);

    const [attachmentsTypes, setAttachmentsTypes] = useRecoilState(attachmentsTypeState);

    const [openUploadAttachmentMenu, setOpenUploadAttachmentMenu] = useState(false);
    const [selectedAttchType, setSelectedAttchType] = useState<string | null>(null);

    const uploadAttachmentActionRef = useRef<HTMLButtonElement>(null);
    let inputTypeAttachmentsRef: any = useRef<any>({});

    const viewSnackbarMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: type == snackbarTypes.error ? t(message, t('common.message.error.tryLater')) : t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const fetchExpiredAttachmentsTypesQuery = useQuery(
        ['fetchExpiredAttachmentsTypes', {id: props.expiredCode}],
        () => fetchExpiredAttachmentsTypes(),
        {
            enabled: false,
            onSuccess: (response: any) => {
                setAttachmentsTypes(response.data);
            },
            onError: (error: AxiosError) => {
                viewSnackbarMessage('translation:error.tryLater');
            }
        })

    const fetchExpiredAttachmentsUploadMutation = useMutation(
        fetchExpiredAttachmentsUpload,
        {
            onSuccess: async (response) => {
                props.showSnackbar && viewSnackbarMessage('common.message.success', snackbarTypes.success);
                if (!!props.postAction && typeof props.postAction === "function") {
                    props.postAction(response.data);
                }
            },
            onError: (error: any) => {
                viewSnackbarMessage(`common.message.error.addExpiredAttachment.${error?.response?.status}`);
            }
        });

    const handleToggleSelectAttachmentType = () => {
        setOpenUploadAttachmentMenu(!openUploadAttachmentMenu);
    }

    const handleCloseInsertAction = () => {
        setOpenUploadAttachmentMenu(false);
    }

    const handleListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            handleCloseInsertAction();
        } else if (event.key === 'Escape') {
            handleCloseInsertAction();
        }
    }

    const uploadAttachment = (attachmentType: string) => {
        if (!!attachmentType && !!inputTypeAttachmentsRef) {
            setSelectedAttchType(attachmentType);
            inputTypeAttachmentsRef.current.click();
        }
    }

    const handleAttachmentsOnChange = async (e: React.BaseSyntheticEvent) =>{
        e.stopPropagation();
        if (!!e.target.files[0]?.size) {
            const resizedFile = await resizeImg(e.target.files[0])
            const formData = new FormData();
            formData.append('expiredId', props.expiredCode);
            formData.append('type', selectedAttchType as any);
            formData.append('attachment', resizedFile as any);

            fetchExpiredAttachmentsUploadMutation.mutate(formData);
            inputTypeAttachmentsRef.current.value = '';
        }
    }

    useEffect(() => {
        if (attachmentsTypes.length == 0) {
            fetchExpiredAttachmentsTypesQuery.refetch();
        }
    }, []);

    return (
        <>
            <input hidden
                   type="file"
                   ref={inputTypeAttachmentsRef}
                   onChange={handleAttachmentsOnChange}
            />

            <Button onClick={handleToggleSelectAttachmentType}
                    endIcon={!!props.endIcon ? <UploadRounded /> : undefined}
                    ref={uploadAttachmentActionRef}
                    variant={props.variant || 'outlined'}
                    sx={{ml: theme.spacing(1)}}
            >
                {props.label ? t(props.label) : t('common.button.uploadAttachment')}
            </Button>

            <Popper
                open={openUploadAttachmentMenu}
                anchorEl={uploadAttachmentActionRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
            >
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleCloseInsertAction}>
                                <MenuList
                                    autoFocusItem={openUploadAttachmentMenu}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    {
                                        attachmentsTypes?.map((type: string) =>
                                                <MenuItem
                                                    key={type}
                                                    onClick={() => uploadAttachment(type)}
                                                >
                                                    {t(`common.attachmentType.${type}`)}
                                                </MenuItem>
                                            )
                                    }
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <CustomLoader loading={fetchExpiredAttachmentsUploadMutation.isLoading || fetchExpiredAttachmentsTypesQuery.isFetching}/>
        </>
    );
}

export default UploadAttachment;